import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { reasonOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Alert, Form } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React, { useEffect } from 'react';

type SubmitIssueP45ModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (params: SubmitIssueP45Values) => void;
  title?: string;
  defaultDate?: Moment | null;
  /** NX-3476: shown when the P45 proceeds without a pension refund, so the
   * user sees WHY none was raised (e.g. outside the 30-day opt-out window). */
  notice?: string;
};

export type SubmitIssueP45Values = {
  dateLeft: Moment;
  reason: Option<string>;
};

const SubmitIssueP45Modal: React.FC<SubmitIssueP45ModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  title,
  defaultDate,
  notice,
}: SubmitIssueP45ModalProps) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm();

  // Sync defaultDate into form whenever the modal opens or defaultDate changes
  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({ dateLeft: defaultDate ?? moment() });
    }
  }, [isOpen, defaultDate]);

  const onFinish: FormProps['onFinish'] = (values) => {
    onSubmit(values);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title ?? messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      minHeight="200px"
    >
      {notice && (
        <Alert
          type="info"
          showIcon
          message={notice}
          style={{ marginBottom: 16 }}
        />
      )}
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        initialValues={{
          dateLeft: defaultDate ?? moment(),
        }}
      >
        <Form.Item
          label={messages['common.dateLeft']}
          name="dateLeft"
          rules={[
            { required: true, message: messages['form.pleaseSelect'] },
          ]}
        >
          <AppDatePicker
            placeholder={messages['common.pleaseSelect']}
            format="DD/MM/YYYY"
          />
        </Form.Item>
        <Form.Item
          label={messages['common.reason']}
          name="reason"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppSelect
            options={reasonOptions}
            placeholder={messages['form.select']}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default SubmitIssueP45Modal;
